import { getFinalFormattedValue, getFormatPattern, getFormattingFromColumn, getLocale, } from '@thoughtspot/formatter-service';
import { getValueFormattedByConfig } from '@thoughtspot/number-format-util';
import { hexToRgbA, isCSSVariableApplied, resolveCSSVariable, } from '@thoughtspot/radiant-react/widgets/color/color-utils';
import { isChartSettingsV2Enabled, isFullKPIValueOnTooltipEnabled, isKPIChartEnhancementsEnabled, } from '@thoughtspot/session-service';
import { get, omit } from 'lodash';
import { useCallback, useMemo } from 'react';
import { ChartEventTypes } from '../../..';
import { getBrightenedColor } from '../../../core/chart-color-utils';
import { getConditionalFormats, getStyles, isConditionSatisfied, } from '../../../core/conditional-formatting-utils';
import { getAbbreviatedPreviousDate, getAbsoluteDifferenceFormatted, getChangeColor, getColorCodeByKeyAndIndexFromGradientColorsPalette, getColumnDataValueByColumnId, getDefaultChangeStyle, getFullKPIValueOnHover, getKeyByColorCodeFromDefaultColorsPalette, getKeyByColorCodeFromGradientColorsPalette, getKpiColumnProperties, getKpiDisplayProperties, getRefTupleDateTimeLabel, isLabelEnabled, } from '../kpi-utils';
export function useKPIContentData(props) {
    var _a, _b, _c, _d;
    const { chartModel, env, chartObj, eventsMap, model } = props;
    const { yAxisColumn, isYAxisColumnMeasure, effectiveRefTuple, currentRefTuple, isChartTimeSeries, isAggregationEnabled, showComparison, heroDataValue, effectiveHeroDataValue, shouldShowNoDataFound, } = model;
    const columnDataLite = get(chartModel, 'data.0.columnDataLite', []);
    const isKpiStyleEnhancementEnabled = isKPIChartEnhancementsEnabled();
    const kpiColumnProps = useMemo(() => getKpiColumnProperties(chartModel), [
        chartModel,
    ]);
    const isKPIChartEnhancementFeatureEnabled = isChartSettingsV2Enabled() && isKpiStyleEnhancementEnabled;
    const kpiDisplayProps = useMemo(() => getKpiDisplayProperties(chartModel), [
        chartModel,
    ]);
    const yColumnFormat = useMemo(() => getFormattingFromColumn(yAxisColumn), [
        yAxisColumn,
    ]);
    const aggregationGranularity = isAggregationEnabled
        ? get(effectiveRefTuple, 'aggregationGranularity')
        : 'DAY';
    const currentDateTime = useMemo(() => getRefTupleDateTimeLabel(chartModel, currentRefTuple, isAggregationEnabled, aggregationGranularity), [
        chartModel,
        currentRefTuple,
        isAggregationEnabled,
        aggregationGranularity,
    ]);
    const refDateTime = useMemo(() => showComparison
        ? getRefTupleDateTimeLabel(chartModel, effectiveRefTuple, isAggregationEnabled, aggregationGranularity)
        : null, [
        showComparison,
        chartModel,
        effectiveRefTuple,
        isAggregationEnabled,
        aggregationGranularity,
    ]);
    const isPresentModeOn = (_a = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _a === void 0 ? void 0 : _a.shouldScaleFonts;
    const isKPIWithAnomalyV2Enabled = (_b = env === null || env === void 0 ? void 0 : env.flags) === null || _b === void 0 ? void 0 : _b.isKPIWithAnomalyV2Enabled;
    const shouldApplyLiveboardKpiFontSize = isKpiStyleEnhancementEnabled &&
        (((_c = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _c === void 0 ? void 0 : _c.currentAnswerMode) === 'PINBOARD_TILE' ||
            ((_d = chartObj === null || chartObj === void 0 ? void 0 : chartObj.chartOptions) === null || _d === void 0 ? void 0 : _d.currentAnswerMode) === 'PRESENT');
    const isPresentModeWithKpiFontSize = isPresentModeOn && shouldApplyLiveboardKpiFontSize;
    const isPresentModeWithoutKpiFontSize = isPresentModeOn && !shouldApplyLiveboardKpiFontSize;
    const conditionalFormats = useMemo(() => {
        var _a;
        return getConditionalFormats((_a = chartModel.vizProp) !== null && _a !== void 0 ? _a : undefined, yAxisColumn === null || yAxisColumn === void 0 ? void 0 : yAxisColumn.id, 'ChartVizProps', chartModel);
    }, [chartModel, yAxisColumn]);
    const applicableCondition = useMemo(() => conditionalFormats.find(conditionalFormat => {
        const lhsColumnValue = conditionalFormat.lhsColumnId === (yAxisColumn === null || yAxisColumn === void 0 ? void 0 : yAxisColumn.id)
            ? heroDataValue
            : getColumnDataValueByColumnId(columnDataLite, conditionalFormat.lhsColumnId);
        const rhsColumnValue = conditionalFormat.rhsColumnId === (yAxisColumn === null || yAxisColumn === void 0 ? void 0 : yAxisColumn.id)
            ? heroDataValue
            : getColumnDataValueByColumnId(columnDataLite, conditionalFormat.rhsColumnId);
        return isConditionSatisfied(conditionalFormat, heroDataValue, chartModel.inScopeParameters, lhsColumnValue, rhsColumnValue);
    }), [
        conditionalFormats,
        yAxisColumn,
        heroDataValue,
        columnDataLite,
        chartModel.inScopeParameters,
    ]);
    const fontConditionalStyles = useMemo(() => {
        const conditionalStyle = applicableCondition
            ? getStyles(applicableCondition, false)
            : undefined;
        return omit(conditionalStyle, 'backgroundColor');
    }, [applicableCondition]);
    const formattedHeroDataValue = useMemo(() => getFinalFormattedValue(effectiveHeroDataValue, yAxisColumn), [effectiveHeroDataValue, yAxisColumn]);
    const refDataValue = showComparison ? effectiveRefTuple === null || effectiveRefTuple === void 0 ? void 0 : effectiveRefTuple.value : undefined;
    const formattedRefDataValue = useMemo(() => !showComparison || !refDataValue || !yColumnFormat
        ? refDataValue
        : getValueFormattedByConfig(refDataValue, yColumnFormat, {
            formatPattern: getFormatPattern(yAxisColumn),
            columnLocale: getLocale(yAxisColumn),
        }), [showComparison, refDataValue, yColumnFormat, yAxisColumn]);
    const diffValue = showComparison
        ? parseFloat((parseFloat(String(get(effectiveRefTuple, 'relativePercentChange'))) || 0).toFixed(2))
        : 0;
    const { color, bgColor, changeDirection } = useMemo(() => {
        if (!showComparison)
            return { color: undefined, bgColor: null, changeDirection: null };
        const unresolved = getChangeColor(diffValue, chartModel, isKPIWithAnomalyV2Enabled, kpiDisplayProps.kpiDisplayColorArray);
        const resolved = resolveCSSVariable(unresolved);
        const variableApplied = isCSSVariableApplied(unresolved);
        const defaultChangeStyle = getDefaultChangeStyle(diffValue, chartModel, isKPIWithAnomalyV2Enabled, kpiDisplayProps.kpiDisplayColorArray);
        if (defaultChangeStyle) {
            return {
                color: defaultChangeStyle.iconColor,
                bgColor: null,
                changeDirection: defaultChangeStyle.direction,
            };
        }
        let bg = null;
        if (variableApplied) {
            bg = hexToRgbA(unresolved, 0.1);
        }
        else if (isKPIWithAnomalyV2Enabled) {
            const key = getKeyByColorCodeFromGradientColorsPalette(resolved) ||
                getKeyByColorCodeFromDefaultColorsPalette(resolved);
            if (key) {
                bg =
                    getColorCodeByKeyAndIndexFromGradientColorsPalette(key) ||
                        getBrightenedColor(resolved);
            }
        }
        return { color: resolved, bgColor: bg, changeDirection: null };
    }, [
        showComparison,
        diffValue,
        chartModel,
        isKPIWithAnomalyV2Enabled,
        kpiDisplayProps.kpiDisplayColorArray,
    ]);
    const formattedAbsoluteDifference = useMemo(() => showComparison
        ? getAbsoluteDifferenceFormatted(effectiveRefTuple, currentRefTuple, yAxisColumn)
        : null, [showComparison, effectiveRefTuple, currentRefTuple, yAxisColumn]);
    const showSublabel = isChartTimeSeries && isLabelEnabled(chartModel);
    const shouldShowAbbreviatedPreviousDate = !!(isKPIChartEnhancementFeatureEnabled &&
        kpiColumnProps.showAbbreviatedPreviousDate);
    const abbreviatedPreviousDate = useMemo(() => getAbbreviatedPreviousDate(chartModel, shouldShowAbbreviatedPreviousDate, kpiColumnProps), [chartModel, shouldShowAbbreviatedPreviousDate, kpiColumnProps]);
    const shouldShowComparisonDate = isKPIChartEnhancementFeatureEnabled &&
        kpiColumnProps.showComparisonDate;
    const kpiTooltipValue = useMemo(() => isFullKPIValueOnTooltipEnabled() && effectiveHeroDataValue != null
        ? getFullKPIValueOnHover(yAxisColumn, effectiveHeroDataValue, formattedHeroDataValue)
        : formattedHeroDataValue, [yAxisColumn, effectiveHeroDataValue, formattedHeroDataValue]);
    const onPointClick = useCallback(() => {
        var _a;
        (_a = eventsMap === null || eventsMap === void 0 ? void 0 : eventsMap.get(ChartEventTypes.PointClick)) === null || _a === void 0 ? void 0 : _a();
    }, [eventsMap]);
    const onPointDoubleClick = useCallback(() => {
        var _a;
        (_a = eventsMap === null || eventsMap === void 0 ? void 0 : eventsMap.get(ChartEventTypes.PointDoubleClick)) === null || _a === void 0 ? void 0 : _a();
    }, [eventsMap]);
    const { showChange, showChangeAs } = kpiDisplayProps;
    const { showCurrentDateLabel, showPreviousDateLabel, showPreviousValue, handleNullData, } = kpiColumnProps;
    const comparisonDisplayProps = useMemo(() => ({ showChange, showChangeAs }), [showChange, showChangeAs]);
    const comparisonColumnProps = useMemo(() => ({
        showComparisonDate: shouldShowComparisonDate,
        abbreviatedPreviousDate,
        showAbbreviatedPreviousDate: shouldShowAbbreviatedPreviousDate,
        showCurrentDateLabel,
        showPreviousDateLabel,
        showPreviousValue,
        handleNullData,
    }), [
        shouldShowComparisonDate,
        abbreviatedPreviousDate,
        shouldShowAbbreviatedPreviousDate,
        showCurrentDateLabel,
        showPreviousDateLabel,
        showPreviousValue,
        handleNullData,
    ]);
    const comparisonFlags = useMemo(() => ({
        isKPIChartEnhancementFeatureEnabled,
        isKPIWithAnomalyV2Enabled,
    }), [isKPIChartEnhancementFeatureEnabled, isKPIWithAnomalyV2Enabled]);
    const shouldShowSubLabel = !isAggregationEnabled &&
        showSublabel &&
        (!isKPIChartEnhancementFeatureEnabled || !!showCurrentDateLabel);
    return {
        yAxisColumn,
        isYAxisColumnMeasure,
        heroDataValue,
        effectiveHeroDataValue,
        formattedHeroDataValue,
        shouldShowNoDataFound,
        isKPIChartEnhancementFeatureEnabled,
        kpiDisplayProps,
        conditionalFormats,
        applicableCondition,
        fontConditionalStyles,
        currentDateTime,
        refDateTime,
        isPresentModeOn,
        isKPIWithAnomalyV2Enabled,
        shouldApplyLiveboardKpiFontSize,
        isPresentModeWithKpiFontSize,
        isPresentModeWithoutKpiFontSize,
        diffValue,
        color,
        bgColor,
        changeDirection,
        formattedRefDataValue,
        formattedAbsoluteDifference,
        showSublabel,
        shouldShowSubLabel,
        kpiTooltipValue,
        comparisonDisplayProps,
        comparisonColumnProps,
        comparisonFlags,
        onPointClick,
        onPointDoubleClick,
    };
}
//# sourceMappingURL=use-kpi-content-data.js.map